import { useShowToast } from '@/hooks';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import {
  useFetchScorecardByIdQuery,
  useSubmitScoreCardMutation,
} from '@/store/query/ats-applications';
import { useLazyGetInterviewTranscriptQuery } from '@/store/query/interviews';
import { KText } from '@/ui-library/components/text';
import {
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Icon,
  Flex,
  Link,
  Box,
  Divider,
} from '@chakra-ui/react';
import { XIcon } from '@heroicons/react/solid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { memo } from 'react';
import { useSelector } from 'react-redux';
import { Hackerrank } from '@/assets/icons/ats';
import { CenterSpinner } from '../loadders';
import { AutoSaveLoadingIndicator } from './AutoSaveLoadingIndicator';
import { ScorecardBody } from './ScorecardBody';
import { useScorecardAutoSaveHelper } from './useScorecardAutoSaveHelper';
import { EmptyResults } from '@/components/ats/empty-results';
import NoTask from '@/assets/icons/ats/NoTask';
import { InterviewTabs } from '@/pages/interviews-revamp/component';
import { AfterInterviewAssistant } from '@/pages/interviews-revamp/after-interview';
import { SliderButtons } from '@/components/candidate-job-slider-v1/components/SliderButtons';
import { useQuery } from '@tanstack/react-query';
import { LinkExternal02 } from '@kula-ai/icons/dual-tone/LinkExternal02';
import { getInterviewById } from '@/pages/interviews-revamp/api';
import type { InterviewType } from '@/pages/interviews-revamp/types/interview';
import {
  TRANSCRIPT_TABS,
  type CollectScoreCardDrawerApplication,
  type CollectScoreCardDrawerAppData,
  type CollectScoreCardDrawerParams,
  type CollectScoreCardDrawerV1Props,
  type DrawerFooterSectionProps,
  type DrawerRightSectionProps,
  type DrawerRightSectionWithQueryProps,
  type ScoreCardDrawerFormRef,
} from './CollectScoreCardDrawerV1.interface';

const DrawerFooterSection = memo(function DrawerFooterSection({
  onClose,
  onSubmitClick,
  isSubmitting,
  isSaved,
}: DrawerFooterSectionProps) {
  return (
    <DrawerFooter borderTop="1px solid" borderColor="border-color">
      <Flex gap="12px" alignItems="center">
        <AutoSaveLoadingIndicator
          containerProps={{ pr: '0' }}
          isSaved={isSaved}
        />
        <Button
          variant="secondary"
          onClick={onClose}
          data-test-id="secondary-cta-button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSubmitClick}
          data-test-id="primary-cta-button"
          isLoading={isSubmitting}
        >
          Submit scorecard
        </Button>
      </Flex>
    </DrawerFooter>
  );
});
DrawerFooterSection.displayName = 'DrawerFooterSection';

// Stable config to avoid re-renders; defined outside so ref is shared
const ASSISTANT_CONTAINER_PROPS = {
  top: '6px',
  left: '6px',
  right: '6px',
  bottom: '56px',
};
const TRIGGER_CONTAINER_PROPS = { mt: '0px' };

const DrawerRightSection = memo(function DrawerRightSection({
  interview,
  interviewId,
}: DrawerRightSectionProps) {
  return (
    <Flex
      h="100%"
      w="100%"
      flexDir="column"
      overflow="hidden"
      position="relative"
    >
      <Flex flex="1" h="100%" w="100%" overflow="hidden">
        <InterviewTabs
          interview={interview}
          interviewId={interviewId}
          tabs={TRANSCRIPT_TABS}
        />
      </Flex>
      <Box p="6px">
        <AfterInterviewAssistant
          interview={interview}
          interviewId={interviewId}
          assistantContainerProps={ASSISTANT_CONTAINER_PROPS}
          triggerContainerProps={TRIGGER_CONTAINER_PROPS}
        />
      </Box>
    </Flex>
  );
});

const DrawerRightSectionWithQuery = memo(function DrawerRightSectionWithQuery({
  interviewId,
}: DrawerRightSectionWithQueryProps) {
  const { data: interviewResponse, isLoading } = useQuery({
    queryKey: ['interview', interviewId],
    queryFn: () => getInterviewById(String(interviewId ?? '')),
    enabled: !!interviewId,
  });
  const interview = interviewResponse?.data;

  if (isLoading || !interview) {
    return (
      <Center h="100%" w="100%">
        <CenterSpinner />
      </Center>
    );
  }
  return (
    <DrawerRightSection
      interview={interview as InterviewType}
      interviewId={interviewId}
    />
  );
});

const defaultApplication: CollectScoreCardDrawerApplication = {};
const defaultParams: CollectScoreCardDrawerParams = {};
const defaultAppData: CollectScoreCardDrawerAppData = {};

export const CollectScoreCardDrawerV1 = ({
  isOpen = true,
  onClose = () => {},
  application = defaultApplication,
  onSuccess = () => {},
  params = defaultParams,
  showOnlyCurrUser = false,
  canFillForOthers = false,
  scoreCardID = null,
  scorecard = null,
  viewSc = false,
  appData = defaultAppData,
  isViewOnly = false,
  isDefaultEdit = false,
  isInterview = undefined,
  interviewId: interviewIdProp = undefined,
  showCandidateLink = false,
  isCurrent = false,
  ...props
}: CollectScoreCardDrawerV1Props) => {
  const formRef = useRef<ScoreCardDrawerFormRef | null>(null);
  const {
    onNext = () => {},
    onPrev = () => {},
    showNavButtons = false,
    scorecards = [],
    headerAddons = null,
  } = props;

  const currentScoreCardId = scoreCardID ?? scorecard?.id;

  const {
    data: dataById,
    isLoading: isLoadingById,
    isFetching: isFetchingById,
  } = useFetchScorecardByIdQuery(
    {
      applicationId: application?.id,
      scorecardId: currentScoreCardId,
    },
    { skip: !scoreCardID }
  );

  // if scorecard is provided, use it, otherwise fetch the data from the API
  const data = useMemo(
    () => (scorecard ? [scorecard] : [dataById]),
    [scorecard, dataById]
  );
  const [isEdit, setIsEdit] = useState(isDefaultEdit);
  const [submitScoreCard, { isLoading: isSubmitting }] =
    useSubmitScoreCardMutation();
  const currUser = useSelector(selectCurrUser);
  const toast = useShowToast();
  const scoreCardReviewerIds = data?.map((sc) => sc?.reviewer?.id);
  const scorecardRowForFooter = showOnlyCurrUser
    ? data?.find((item) => item?.reviewer?.id === currUser?.id)
    : data?.[0];
  /** Submitted scorecard opened in view-only / viewSc — hide submit footer. */
  const isSubmittedReadOnlyMode =
    scorecardRowForFooter?.status === 'submitted' &&
    (isViewOnly || viewSc) &&
    !isEdit;
  /** Activity closed when enough scorecards submitted (API `status`). */
  const isScorecardClosed = !!data?.[0] && data?.[0]?.status === 'closed';
  const showScoreCard = Boolean(data?.[0]) && !isScorecardClosed;
  const canSubmit =
    isEdit &&
    (canFillForOthers ||
      application?.ats_job?.can_edit ||
      scoreCardReviewerIds?.includes(currUser?.id));
  const showFooter =
    (data?.length === 1 || showOnlyCurrUser) &&
    canSubmit &&
    !isViewOnly &&
    !isSubmittedReadOnlyMode &&
    !isScorecardClosed;

  const interviewId =
    interviewIdProp ?? appData?.id ?? params?.ats_interview_id;

  // When scorecard is not passed, fetch interview to determine transcript visibility (backward compat)
  const { data: interviewForVisibilityResponse } = useQuery({
    queryKey: ['interview', interviewId],
    queryFn: () => getInterviewById(String(interviewId ?? '')),
    enabled: isInterview && !!interviewId,
  });

  const hasTranscriptData =
    interviewForVisibilityResponse?.data?.transcript_status === 'completed';

  const [getInterviewTranscript, { data: transcriptData }] =
    useLazyGetInterviewTranscriptQuery();

  useEffect(() => {
    // Fetch the transcript whenever the interview reports it as completed —
    // either from the freshly fetched interview (drives the right summary
    // panel) or a fresh appData prop. Relying on appData alone hid the
    // "Fill with AI" button when the prop was stale but the interview was done.
    if (
      (hasTranscriptData || appData?.transcript_status === 'completed') &&
      interviewId
    ) {
      getInterviewTranscript({ interviewId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTranscriptData, appData?.transcript_status, interviewId]);
  const { handleAutoSave, isSaved } = useScorecardAutoSaveHelper({
    atsApplicationId: application?.id,
    scorecardId: currentScoreCardId,
    questions: data?.[0]?.questions,
  });

  // handle Single scorecard – memoized so ScorecardBody doesn't re-render on parent updates
  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      const questions = data?.[0]?.questions;
      const currUserScorecard = data?.find(
        (item) => item?.reviewer?.id === currUser?.id
      );
      let scorecardId;
      if (showOnlyCurrUser) {
        scorecardId = currUserScorecard?.id;
      } else {
        scorecardId = data?.[0]?.id;
      }
      const applicationId = application?.id;
      const payload = {
        rating: values?.rating,
        feedback: values?.feedback,
        questions: questions?.map((item, idx) => {
          const param: { question: unknown; answer?: unknown } = {
            question: item?.question,
          };
          if (values?.[idx + 1]) (param as any).answer = values?.[idx + 1];
          return param;
        }),
        status: 'submitted',
      };
      const result = await submitScoreCard({
        applicationId,
        scorecardId,
        payload,
      });
      const submitResult = result as { error?: { length?: number } };
      if (submitResult?.error?.length) {
        toast({ title: 'Scorecard submission failed', status: 'error' });
      } else {
        toast({ title: 'Scorecard submitted successfully' });
        onSuccess();
        onClose();
      }
    },
    [
      data,
      application?.id,
      currUser?.id,
      showOnlyCurrUser,
      submitScoreCard,
      toast,
      onSuccess,
      onClose,
    ]
  );

  const closeDrawer = useCallback(() => {
    onClose();
  }, [onClose]);
  // Check if fetched data matches the requested scoreCardID to prevent stale data display
  const isDataStale =
    currentScoreCardId && dataById?.id && dataById.id !== currentScoreCardId;
  const isLoadingOrStale = isLoadingById || isFetchingById || isDataStale;

  // Memoized so SliderButtons doesn't re-render when parent updates
  const applicationsForNav = useMemo(
    () => scorecards?.map((sc) => ({ appId: String(sc.id) })),
    [scorecards]
  );
  const applicationForNav = useMemo(
    () => ({ appId: String(currentScoreCardId ?? '') }),
    [currentScoreCardId]
  );

  const handleSubmitFormClick = useCallback(() => {
    if (formRef?.current) formRef.current.submitForm();
    const errArray = Object.keys(formRef?.current?.errors ?? {});
    if (errArray?.length > 0) {
      const element = document.querySelector(`label[for="${errArray[0]}"]`);
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, []);

  // When scorecard is passed (e.g. scorecard-visibility flow), use can_view_interview; else fall back to transcript completion for existing callers
  const showRightSection = isInterview && !!interviewId && hasTranscriptData;
  // (scorecard !== null
  //   ? Boolean(scorecard?.record?.can_view_interview)
  //   : hasTranscriptData);
  const drawerContentWidth =
    showRightSection && showScoreCard && !isScorecardClosed ? '90%' : '600px';

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={closeDrawer}
      closeOnOverlayClick={!showRightSection}
    >
      <DrawerOverlay zIndex="1400" />
      <DrawerContent
        borderTopLeftRadius="xl"
        w={drawerContentWidth}
        maxW={drawerContentWidth}
        transition="width 0.3s ease, max-width 0.3s ease"
        data-test-id="collect-score-card-drawer-v1"
      >
        {showNavButtons ? (
          <SliderButtons
            onClose={closeDrawer}
            onNext={onNext}
            onPrev={onPrev}
            applications={applicationsForNav}
            application={applicationForNav}
            showNavItems={showNavButtons}
          />
        ) : (
          <Center
            w="48px"
            h="48px"
            left="-70px"
            top="32px"
            pos="absolute"
            bg="#fff"
            borderRadius="full"
            onClick={closeDrawer}
            cursor="pointer"
          >
            <Icon
              as={XIcon}
              w="20px"
              h="20px"
              data-test-id="scorecard-drawer-close-icon"
            />
          </Center>
        )}

        <DrawerHeader p="12px 24px">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex alignItems="center" gap="8px">
              <KText size="md-medium" data-test-id="scorecard-drawer-header">
                {isScorecardClosed ? 'View ' : viewSc ? 'View ' : 'Fill out '}
                scorecard
              </KText>
              {headerAddons}
            </Flex>
            {data?.[0]?.record?.ext_interview_record
              ?.ext_interview_report_url && (
              <Link
                href={
                  data[0].record.ext_interview_record.ext_interview_report_url
                }
                target="_blank"
                display="flex"
                alignItems="center"
                gap="6px"
                _hover={{ textDecoration: 'none' }}
              >
                <Box bg="secondary-100" borderRadius="sm" p="6px 12px">
                  <Flex alignItems="center" gap="6px">
                    <Icon as={Hackerrank} width="18px" height="18px" />
                    <KText
                      size="sm-semi-bold"
                      color="primary-600"
                      _hover={{
                        textDecoration: 'underline',
                        color: 'primary-600',
                        cursor: 'pointer',
                      }}
                    >
                      View report
                    </KText>
                  </Flex>
                </Box>
              </Link>
            )}
            {isInterview &&
              !isCurrent &&
              (scorecard?.record?.can_view_interview ?? hasTranscriptData) && (
                <Link
                  href={`/interviews/${scorecard?.record?.id ?? interviewId}`}
                  target="_blank"
                  _focus={{ boxShadow: 'none' }}
                >
                  <Button variant="tertiary" gap="12px" alignItems="center">
                    <Icon as={LinkExternal02} width="18px" height="18px" />
                    <KText size="sm-medium" color="primary-700">
                      Go to full interview
                    </KText>
                  </Button>
                </Link>
              )}
          </Flex>
        </DrawerHeader>
        <Divider />

        <DrawerBody p="0" {...(isInterview ? { overflowY: 'hidden' } : {})}>
          {isLoadingOrStale ? (
            <CenterSpinner />
          ) : isScorecardClosed ? (
            <Center h="80vh">
              <EmptyResults
                icon={NoTask}
                wrapperProps={{ h: '100%', flex: 1 }}
                subText="CLOSED"
                subTextProps={{ color: 'secondary-600' }}
                text="This review has been closed"
                textProps={{ color: 'secondary-900' }}
                placeholder="Enough scorecards have been submitted for this activity."
                iconProps={{ h: '140px', w: '130px', mb: '0px' }}
              />
            </Center>
          ) : !showScoreCard ? (
            <Center h="80vh">
              <EmptyResults
                icon={NoTask}
                wrapperProps={{ h: '100%', flex: 1 }}
                subText="SCORECARD UNAVAILABLE"
                subTextProps={{ color: 'secondary-600' }}
                text="This scorecard is no longer accepting responses"
                textProps={{ color: 'secondary-900' }}
                iconProps={{ h: '140px', w: '130px', mb: '0px' }}
              />
            </Center>
          ) : null}
          {showScoreCard && !isLoadingOrStale ? (
            <Flex w="100%" overflow="hidden" h="100%">
              <Flex
                w={showRightSection ? '55%' : '100%'}
                maxW={showRightSection ? '55%' : '100%'}
                borderRight="1px solid"
                borderColor="border-color"
                overflowY="auto"
                h="100%"
                transition="all 0.3s ease"
              >
                <ScorecardBody
                  key={currentScoreCardId}
                  scoreCardDetails={data}
                  formRef={formRef}
                  handleSubmit={handleSubmit as unknown as () => void}
                  onSuccess={onSuccess}
                  showOnlyCurrUser={showOnlyCurrUser}
                  canFillForOthers={canFillForOthers}
                  canSubmit={canSubmit}
                  viewSc={viewSc}
                  transcript={transcriptData}
                  appData={appData}
                  isViewOnly={isViewOnly}
                  isEdit={isEdit}
                  setIsEdit={setIsEdit as unknown as () => void}
                  handleAutoSave={handleAutoSave}
                  applicationId={application?.id}
                  jobId={application?.ats_job?.id}
                  showCandidateLink={showCandidateLink}
                />
              </Flex>
              {showRightSection && (
                <Flex w="45%" maxW="45%" h="100%" transition="all 0.3s ease">
                  <DrawerRightSectionWithQuery interviewId={interviewId} />
                </Flex>
              )}
            </Flex>
          ) : null}
        </DrawerBody>
        {showFooter && (
          <DrawerFooterSection
            onClose={closeDrawer}
            onSubmitClick={handleSubmitFormClick}
            isSubmitting={isSubmitting}
            isSaved={isSaved}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
};
